'use client';

import { useTranslations } from 'next-intl';
import { type FC, memo } from 'react';
import { cn } from '@/app/utils';

const AWARD_CONTAINER_STYLES =
  'flex items-center justify-center gap-1 rounded-md bg-black/70 px-2 py-1 backdrop-blur-[15px]';

/** 奖项等级渐变色配置 */
const PRIZE_GRADIENTS = {
  first: 'bg-[linear-gradient(0deg,#FF6044_0%,#FFD8B1_75.74%)]',
  second: 'bg-[linear-gradient(0deg,#8559FF_0%,#E5DCFF_75.74%)]',
  third: 'bg-[linear-gradient(0deg,#1B85FF_0%,#9FE5FF_75.74%)]',
  creative: 'bg-[linear-gradient(0deg,#EE9C0D_0%,#FFEDB3_75.74%)]',
} as const;

/** 奖项文字样式 */
const AwardText: FC<{ gradient: string; children: React.ReactNode }> = ({ gradient, children }) => (
  <span className={cn(gradient, 'bg-clip-text font-semibold text-transparent text-xs')}>{children}</span>
);

/** 创意奖图标 */
export const CreativeAwardIcon = memo(function CreativeAwardIcon({
  name,
  className,
}: {
  name: string;
  className?: string;
}) {
  return (
    <div className={cn(AWARD_CONTAINER_STYLES, 'self-stretch overflow-hidden', className)}>
      <CreativeAwardSvg />
      <AwardText gradient={PRIZE_GRADIENTS.creative}>{name}</AwardText>
    </div>
  );
});

/** 等级奖项组件 */
const PrizeIcon = memo(function PrizeIcon({
  level,
  className,
}: {
  level: 'first' | 'second' | 'third';
  className?: string;
}) {
  const t = useTranslations();
  return (
    <div className={cn(AWARD_CONTAINER_STYLES, 'inline-flex h-6', className)}>
      <AwardText gradient={PRIZE_GRADIENTS[level]}>{t(`activity.worksTab.award.${level}`)}</AwardText>
    </div>
  );
});

export const FirstPrizeIcon = memo(({ className }: { className?: string }) => (
  <PrizeIcon level="first" className={className} />
));
export const SecondPrizeIcon = memo(({ className }: { className?: string }) => (
  <PrizeIcon level="second" className={className} />
));
export const ThirdPrizeIcon = memo(({ className }: { className?: string }) => (
  <PrizeIcon level="third" className={className} />
));

/** 奖项等级映射 */
const PRIZE_LEVEL_MAP: Record<string, FC<{ className?: string }>> = {
  '1': FirstPrizeIcon,
  '2': SecondPrizeIcon,
  '3': ThirdPrizeIcon,
};

interface AwardBadgeProps {
  award?: { name?: string; level?: string };
  className?: string;
  itemClassName?: string;
}

/** 奖项徽章组件（通用） */
export const AwardBadge = memo(function AwardBadge({ award, className, itemClassName }: AwardBadgeProps) {
  if (!award) return null;

  const PrizeComponent = award.level ? PRIZE_LEVEL_MAP[award.level] : null;

  return (
    <div className={className}>
      {award.name && <CreativeAwardIcon name={award.name} className={itemClassName} />}
      {PrizeComponent && <PrizeComponent className={itemClassName} />}
    </div>
  );
});

/** 创意奖 SVG 图标 */
const CreativeAwardSvg = () => (
  <svg width="1em" height="1em" fill="none" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8.82715 1C9.45951 1.00105 10.0665 1.25304 10.5137 1.7002C10.688 1.87462 10.8309 2.07418 10.9424 2.28906H12.3584C12.5185 2.28906 12.6773 2.32058 12.8252 2.38184C12.973 2.44305 13.1076 2.53243 13.2207 2.64551C13.3338 2.75864 13.4241 2.89321 13.4854 3.04102C13.5466 3.18888 13.5781 3.34777 13.5781 3.50781V5.29492C13.5763 6.05924 13.2709 6.79157 12.7295 7.33105C12.2627 7.79618 11.6529 8.08309 11.0039 8.15332C10.9659 8.27378 10.9252 8.39356 10.877 8.51074C10.6534 9.0534 10.3245 9.54721 9.91016 9.96289C9.49596 10.3784 9.0036 10.7083 8.46191 10.9336C8.08154 11.0917 7.68102 11.1958 7.27344 11.2441V11.9365C7.27344 11.9934 7.26906 12.0461 7.25977 12.0938H9.00781C9.30964 12.0938 9.46094 12.245 9.46094 12.5469C9.46085 12.8486 9.30955 12.999 9.00781 12.999H4.49316C4.19149 12.999 4.0411 12.8485 4.04102 12.5469C4.04102 12.2451 4.19141 12.0938 4.49316 12.0938H6.24219C6.23289 12.0461 6.22852 11.9934 6.22852 11.9365V11.2441C5.23768 11.1268 4.30827 10.6803 3.5957 9.96777C3.0808 9.45271 2.70543 8.82432 2.49219 8.1416C1.87216 8.05644 1.2922 7.77462 0.844727 7.32715C0.305626 6.78798 0.00183946 6.05738 0 5.29492V3.50781C4.21904e-05 3.18578 0.127462 2.87682 0.354492 2.64844C0.581559 2.42007 0.889883 2.29098 1.21191 2.28906H2.55762C2.66908 2.07425 2.81121 1.87636 2.98242 1.7041C3.20406 1.48117 3.46758 1.30339 3.75781 1.18262C4.04808 1.0619 4.36044 1.00004 4.6748 1H8.82715ZM6.9375 3.86621C6.86856 3.72774 6.67063 3.72789 6.60156 3.86621L6.07715 4.92578C6.04997 4.98066 5.99704 5.01915 5.93652 5.02832L4.7666 5.2002C4.61307 5.22271 4.55211 5.41107 4.66309 5.51953L5.50781 6.3457C5.55163 6.38868 5.57178 6.4512 5.56152 6.51172L5.36328 7.67676C5.33742 7.82964 5.49835 7.94602 5.63574 7.87402L6.68262 7.3252C6.73694 7.29698 6.80218 7.29684 6.85645 7.3252L7.90332 7.87402C8.04061 7.94561 8.20157 7.82948 8.17578 7.67676L7.97754 6.51172C7.96722 6.45106 7.98724 6.38871 8.03125 6.3457L8.87598 5.51953C8.98648 5.41114 8.92552 5.22301 8.77246 5.2002L7.60254 5.02832C7.54189 5.01929 7.48919 4.98071 7.46191 4.92578L6.9375 3.86621ZM11.2129 3.38574V6.7998C11.2131 6.88361 11.2096 6.96729 11.2051 7.05078C11.4967 6.96487 11.7655 6.80858 11.9844 6.58984C12.3278 6.24644 12.5215 5.78057 12.5225 5.29492V3.50781C12.5224 3.46171 12.5043 3.41738 12.4717 3.38477C12.4391 3.35217 12.3947 3.33406 12.3486 3.33398H11.21C11.2104 3.35123 11.2129 3.36845 11.2129 3.38574ZM1.21191 3.33398C1.16582 3.33406 1.12146 3.35217 1.08887 3.38477C1.05628 3.41738 1.03813 3.46171 1.03809 3.50781V5.29492C1.03901 5.78057 1.23276 6.24644 1.57617 6.58984C1.77923 6.79278 2.02566 6.94099 2.29297 7.03027C2.28942 6.95825 2.28912 6.88592 2.28906 6.81348V3.40039C2.28893 3.37822 2.2915 3.35613 2.29199 3.33398H1.21191Z"
      fill="url(#creative_award_gradient)"
    />
    <defs>
      <linearGradient
        id="creative_award_gradient"
        x1="6.75081"
        y1="12.9991"
        x2="6.75081"
        y2="1.00003"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#EE9C0D" />
        <stop offset="0.895836" stopColor="#FFEDB3" />
      </linearGradient>
    </defs>
  </svg>
);
